import { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { FormField, Input, Select } from '../ui/Form';
import { Save } from 'lucide-react';
import type { AnalyticAccount, CreateAnalyticAccountPayload, AnalyticAccountType } from '../../types/analytic-accounting';
import { ANALYTIC_ACCOUNT_TYPE_LABELS } from '../../types/analytic-accounting';

interface AnalyticAccountFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateAnalyticAccountPayload) => Promise<void>;
  account?: AnalyticAccount | null;
  existingAccounts?: AnalyticAccount[];
}

export function AnalyticAccountFormModal({
  isOpen,
  onClose,
  onSubmit,
  account,
  existingAccounts = [],
}: AnalyticAccountFormModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    type: 'cost_center' as AnalyticAccountType,
    parentId: '',
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (account) {
      setFormData({
        code: account.code,
        name: account.name,
        description: account.description || '',
        type: account.type,
        parentId: account.parentId || '',
        isActive: account.isActive,
      });
    }
  }, [account]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code.trim()) newErrors.code = 'Le code est requis';
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
    // Check code uniqueness (except current account)
    const duplicate = existingAccounts.find(a => a.code === formData.code.trim() && a.id !== account?.id);
    if (duplicate) newErrors.code = 'Ce code existe déjà';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await onSubmit({
        code: formData.code.trim(),
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        type: formData.type,
        parentId: formData.parentId || undefined,
        isActive: formData.isActive,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const typeOptions = Object.entries(ANALYTIC_ACCOUNT_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  // Parents possibles: comptes racine (sans parent) — exclure le compte en cours d'édition
  const parentOptions = existingAccounts
    .filter(a => !a.parentId && a.id !== account?.id)
    .map(a => ({ value: a.id, label: `${a.code} — ${a.name}` }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={account ? 'Modifier le compte analytique' : 'Nouveau compte analytique'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Code" error={errors.code} required>
            <Input
              value={formData.code}
              onChange={(e) => updateField('code', e.target.value)}
              placeholder="Ex: CC-001"
              error={errors.code}
            />
          </FormField>
          <FormField label="Type" required>
            <Select
              value={formData.type}
              onChange={(e) => updateField('type', e.target.value)}
              options={typeOptions}
            />
          </FormField>
        </div>

        <FormField label="Nom" error={errors.name} required>
          <Input
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="Ex: Direction Générale"
            error={errors.name}
          />
        </FormField>

        <FormField label="Description">
          <textarea
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Description du compte analytique"
            rows={2}
            className="form-textarea w-full"
          />
        </FormField>

        {parentOptions.length > 0 && (
          <FormField label="Compte parent" hint="Optionnel — pour créer une hiérarchie analytique">
            <Select
              value={formData.parentId}
              onChange={(e) => updateField('parentId', e.target.value)}
              options={[{ value: '', label: 'Aucun (compte racine)' }, ...parentOptions]}
            />
          </FormField>
        )}

        <FormField label="Statut">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => updateField('isActive', e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-foreground">Compte actif</span>
          </label>
        </FormField>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" icon={Save} isLoading={loading}>
            {account ? 'Mettre à jour' : 'Créer'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
